import { Controller, useForm } from 'react-hook-form'

import { useDialogState } from '@/shared/hooks/useDialogState'
import { useMe } from '@/shared/hooks/useMe'
import { useCreateMember, useUpdateMember } from '@/shared/hooks/useMembers'
import { useSetToast } from '@/shared/hooks/useSetToast'

import type { CreateMember, CreateOrUpdateMemberForm, Member, UpdateMember } from '@/shared/models/memberModel'
import { inputMessageContext, inputRequiredContext, inputStateContext } from '@/shared/utils/inputUtils'
import { emailRegex } from '@/shared/utils/validatorUtils'
import Button from '@/shared/v2/shared/components/Button'
import {
    DrawerBody,
    DrawerClose,
    DrawerContainer,
    DrawerContent,
    DrawerFooter,
    DrawerHeader,
    DrawerRoot,
    DrawerTrigger
} from '@/shared/v2/shared/components/Drawer/Drawer'
import Icon from '@/shared/v2/shared/components/Icon'
import Input from '@/shared/v2/shared/components/Input'
import LoadingButton from '@/shared/v2/shared/components/LoadingButton'
import PrivilegesButton from '@/shared/v2/shared/components/PrivilegesButton'
import Select from '@/shared/v2/shared/components/Select'
import Tooltip from '@/shared/v2/shared/components/Tooltip'
import { Info } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { useEffect, useMemo } from 'react'

const isUpdateMember = (payload: CreateOrUpdateMemberForm): payload is UpdateMember => {
    return 'id' in payload && !!payload.id && !!payload.email && !!payload.name && !!payload.privileges
}

const isCreateMember = (payload: CreateOrUpdateMemberForm): payload is CreateMember => {
    return !!payload.email && !!payload.name && !!payload.privileges
}

const modes = ['create', 'update'] as const
type Mode = (typeof modes)[number]

export type CreateOrUpdateMemberDrawerProps = {
    mode: Mode
    member: Member | null
    onResetMember?: () => void
}

const CreateOrUpdateMemberDrawer = ({ mode, member, onResetMember }: CreateOrUpdateMemberDrawerProps) => {
    const rootTranslations = useTranslations('root')
    const settingsTranslations = useTranslations('settings')
    const toasterTranslations = useTranslations('toasters')

    const defaultValues = useMemo<UpdateMember | undefined>(() => {
        if (member) {
            return {
                id: member.id,
                privileges: member.privileges,
                email: member.email,
                name: member.name
            }
        }
    }, [member])

    const {
        register,
        handleSubmit,
        reset,
        watch,
        formState: { errors },
        control
    } = useForm<CreateOrUpdateMemberForm>({
        defaultValues
    })

    const getInputState = inputStateContext<CreateOrUpdateMemberForm>(watch, errors)
    const getInputMessage = inputMessageContext<CreateOrUpdateMemberForm>(errors)
    const getRequiredValue = inputRequiredContext()

    const setToast = useSetToast()
    const createMember = useCreateMember({ shouldTriggerToaster: false })
    const updateMember = useUpdateMember({ shouldTriggerToaster: false })
    const { isGod } = useMe()

    const hook = mode === 'create' ? createMember : updateMember

    useEffect(() => {
        reset(defaultValues)
    }, [defaultValues, reset])

    const onCreateMember = async (payload: CreateOrUpdateMemberForm) => {
        try {
            if (!modes.includes(mode)) {
                throw new Error('Invalid payload for create or update')
            }

            if (mode === 'update' && isUpdateMember(payload)) {
                const updatePayload: UpdateMember = {
                    id: payload.id,
                    email: payload.email,
                    name: payload.name,
                    privileges: payload.privileges
                }
                await updateMember.mutateAsync(updatePayload)
                setToast({
                    intent: 'success',
                    title: toasterTranslations('updateMemberRole.positive.title'),
                    description: toasterTranslations('updateMemberRole.positive.description')
                })
                return
            }

            if (mode === 'create' && isCreateMember(payload)) {
                const createPayload: CreateMember = {
                    email: payload.email,
                    name: payload.name,
                    privileges: payload.privileges
                }
                await createMember.mutateAsync(createPayload)
                setToast({
                    intent: 'success',
                    title: toasterTranslations('postMember.positive.title'),
                    description: toasterTranslations('postMember.positive.description')
                })
                return
            }
        } catch (error) {
            setToast(
                {
                    intent: 'error',
                    title: toasterTranslations(
                        mode === 'create' ? 'postMember.negative.title' : 'updateMemberRole.negative.title'
                    ),
                    description: toasterTranslations(
                        mode === 'create' ? 'postMember.negative.description' : 'updateMemberRole.negative.description'
                    )
                },
                error
            )
        }
    }

    const { isOpen, onOpenChange } = useDialogState({
        defaultOpen: mode === 'update',
        shouldCloseIf: hook.isSuccess,
        onClose: () => {
            reset()
            hook.reset()
            onResetMember?.()
        }
    })

    return (
        <DrawerRoot open={isOpen} onOpenChange={onOpenChange} modal={true}>
            {mode === 'create' ? (
                <DrawerTrigger asChild>
                    {isGod ? (
                        <Button data-testid="__create-member-god-mode-button__" intent="primary">
                            {settingsTranslations('members.actions.create')}
                        </Button>
                    ) : (
                        <PrivilegesButton data-testid="__create-member-button__" intent="primary">
                            {settingsTranslations('members.actions.create')}
                        </PrivilegesButton>
                    )}
                </DrawerTrigger>
            ) : null}
            <DrawerContent>
                <DrawerContainer as="form" onSubmit={handleSubmit(onCreateMember)}>
                    <DrawerHeader>{settingsTranslations('members.actions.open')}</DrawerHeader>
                    <DrawerBody>
                        <div className="grid grid-cols-1 gap-y-2">
                            <Input
                                data-testid="__member-email-input__"
                                label={settingsTranslations('members.email')}
                                type="email"
                                placeholder={rootTranslations(
                                    'pages.components.createOrUpdateMemberDrawer.text.johnGmailCom'
                                )}
                                state={getInputState('email')}
                                message={getInputMessage('email')}
                                required={true}
                                {...register('email', {
                                    required: getRequiredValue(),
                                    pattern: {
                                        value: emailRegex,
                                        message: rootTranslations('errors.inputs.email')
                                    }
                                })}
                            />
                            <Input
                                data-testid="__member-name-input__"
                                label={settingsTranslations('members.name')}
                                placeholder={rootTranslations(
                                    'pages.components.createOrUpdateMemberDrawer.text.johnDoe'
                                )}
                                state={getInputState('name')}
                                message={getInputMessage('name')}
                                required={true}
                                {...register('name', {
                                    required: getRequiredValue()
                                })}
                            />
                            <Controller
                                control={control}
                                rules={{
                                    required: getRequiredValue()
                                }}
                                name="privileges"
                                render={({ field: { onChange, value } }) => (
                                    <Select
                                        required={true}
                                        data-testid="__member-privileges-select__"
                                        label={
                                            <div className="flex gap-1 justify-start">
                                                <span>{settingsTranslations('members.privileges.title')}</span>
                                                <Tooltip
                                                    trigger={<Icon icon={Info} size="1" />}
                                                    content={settingsTranslations('members.tooltip')}
                                                />
                                            </div>
                                        }
                                        value={
                                            value
                                                ? {
                                                      label: settingsTranslations(`members.privileges.${value}`),
                                                      value
                                                  }
                                                : null
                                        }
                                        state={getInputState('privileges')}
                                        message={getInputMessage('privileges')}
                                        onChange={(options) => onChange(options?.value)}
                                        placeholder={settingsTranslations('members.privileges.placeholder')}
                                        options={[
                                            {
                                                label: settingsTranslations('members.privileges.manager'),
                                                value: 'manager'
                                            },
                                            {
                                                label: settingsTranslations('members.privileges.readwrite'),
                                                value: 'readwrite'
                                            },
                                            {
                                                label: settingsTranslations('members.privileges.readonly'),
                                                value: 'readonly'
                                            }
                                        ]}
                                    />
                                )}
                            />
                        </div>
                    </DrawerBody>
                    <DrawerFooter>
                        <div className="absolute left-0 bottom-0 w-full p-6 grid grid-cols-2 gap-x-3">
                            <DrawerClose asChild>
                                <Button intent="secondary">{settingsTranslations('members.actions.cancel')}</Button>
                            </DrawerClose>
                            <LoadingButton intent="primary" isLoading={hook.isPending} type="submit">
                                {settingsTranslations(`members.actions.${mode}`)}
                            </LoadingButton>
                        </div>
                    </DrawerFooter>
                </DrawerContainer>
            </DrawerContent>
        </DrawerRoot>
    )
}

export default CreateOrUpdateMemberDrawer
